import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../config.js';

import '../styles/global.css';
import '../styles/buttons.css';

import PageHeader from '../components/general/pageHeader.js';
import PageTable from '../components/general/pageTable.js';
import DeleteConfirmation from '../components/general/deleteConfirmation.js';
import ManageForm from '../components/forms/manageForm.js';
import Loading from '../components/general/loading.js';
import useAuthenticate from '../components/authentication/authenticate.js';

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const ManageInvoiceCategories = () => {
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedInvoiceType, setSelectedInvoiceType] = useState(null);
    const [editingInvoiceType, setEditingInvoiceType] = useState(null);
    const [formOverlayOpen, setFormOverlayOpen] = useState(false);
    const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);

    const { isLoading } = useAuthenticate();

    const queryClient = useQueryClient();

    const { data: properties } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties,
    });

    const { data: invoiceType, isLoading: isTableLoading } = useQuery({
        queryKey: ['v2-invoiceType', selectedProperty?.value],
        queryFn: () => fetchInvoiceTypes(),
        enabled: !!selectedProperty,
    });

    // Auto-select first property
    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
            setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    const handleDelete = (invoiceType) => {
        setSelectedInvoiceType(invoiceType);
        setDeleteOverlayOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${config.API_URL}/v2/invoiceType/${selectedInvoiceType._id}`, {
                headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
            });

            setDeleteOverlayOpen(false);
            setSelectedInvoiceType(null);
            await queryClient.invalidateQueries(['invoiceType', selectedProperty?.value]);
            toast.success('Invoice category deleted successfully!');
        } catch (error) {
            console.error('Error deleting invoice type:', error);
            toast.error('Failed to delete invoice category.');
        }
    };

    const handleSubmit = async (formData) => {
        try {
            const invoiceTypeData = {
                type: formData.name,
                _id: formData.id,
                property: selectedProperty?.value
            };

            if (editingInvoiceType) {
                await axios.put(`${config.API_URL}/v2/invoiceType/${invoiceTypeData._id}`, invoiceTypeData, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
                });
                toast.success('Invoice category updated successfully!');
            } else {
                await axios.post(`${config.API_URL}/v2/invoiceType`, invoiceTypeData, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
                });
                toast.success('Invoice category created successfully!');
            }
            setFormOverlayOpen(false);
            setEditingInvoiceType(null);

            // Refresh the vendor list after submission
            await queryClient.invalidateQueries(['invoiceType', selectedProperty?.value]);
        } catch (error) {
            if (editingInvoiceType) {
                toast.error('Failed to update invoice category.');
            } else {
                toast.error('Failed to create invoice category.');
            }
            console.error('Error submitting invoice type:', error);
        }
    };

    return (
        <div className="w-full">
            <PageHeader pageName="Manage Invoice Categories" />
            {isLoading && !selectedProperty ? (
                <Loading />
            ) : (
                <>
                    <div className="page-header">
                        <Autocomplete
                            options={properties}
                            value={selectedProperty}
                            onChange={(e, newVal) => {
                                if (newVal && newVal.value && newVal.label) {
                                    setSelectedProperty(newVal);
                                } else {
                                    setSelectedProperty(null);
                                }
                            }}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) =>
                                typeof option === 'string'
                                    ? option
                                    : option?.label || option?.name || ''
                            }
                            renderInput={params => <TextField {...params} label="Select Property" variant="outlined" fullWidth />}
                            sx={{ width: "350px" }}
                        />
                        <button onClick={() => setFormOverlayOpen(true)} className="header-button">
                            <i className="fas fa-add"></i>Invoice Type
                        </button>
                    </div>
                    {isTableLoading ? (
                        <Loading />
                    ) : (
                        <PageTable
                            rows={invoiceType?.filter(it => it.property?._id === selectedProperty?.value)}
                            columns={[
                                { label: 'Invoice Types', accessor: 'type' },
                            ]}
                            hasButtons={true}
                            hasEdit={true}
                            hasDelete={true}
                            onEdit={(invoiceType) => {
                                setEditingInvoiceType(invoiceType);
                                setFormOverlayOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    )}
                </>
            )}
            {formOverlayOpen && (
                <ManageForm
                    type="Invoice Category"
                    onSubmit={handleSubmit}
                    editingItem={editingInvoiceType}
                    onClose={() => {
                        setFormOverlayOpen(false);
                        setEditingInvoiceType(null);
                    }}
                />
            )}
            {deleteOverlayOpen && (
                <DeleteConfirmation
                    setIsOverlayOpen={setDeleteOverlayOpen}
                    onConfirm={confirmDelete}
                />
            )}
        </div>
    );
}

const fetchProperties = async () => {
    try {
        const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
        return response.data.map(item => ({ value: item._id, label: item.name }));
    } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
    }
};

const fetchInvoiceTypes = async () => {
    try {
        const response = await axios.get(`${config.API_URL}/v2/invoiceType`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
        return response.data;
    } catch (error) {
        console.error("Error fetching invoice types:", error);
        return [];
    }
};

export default ManageInvoiceCategories;